<script setup>
import { ref, reactive, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import { toPng } from 'html-to-image';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import AtalhoAnalitico from './components/AtalhoAnalitico.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import JourneyAPI from 'dashboard/api/fandata/journey';
import DeltaPeriodo from './DeltaPeriodo.vue';
import ParceirosAPI from 'dashboard/api/fandata/parceiros';
import RecontatoAPI from 'dashboard/api/fandata/recontato';

// Customer Journey Flow (T-20): fluxo Sankey da jornada do torcedor (entrada->bot->atendimento->pendência->desfecho).
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const STATUS_LABEL = {
  open: 'Aberta',
  resolved: 'Resolvida',
  pending: 'Pendente',
  snoozed: 'Adiada',
};
const statusLabel = s => STATUS_LABEL[s] || s;

const NODEW = 150;
const COLGAP = 140;
const GAP = 10;
const expanded = ref(false);
const viewportH = ref(typeof window !== 'undefined' ? window.innerHeight : 800);
const flowH = computed(() => (expanded.value ? Math.max(viewportH.value - 250, 460) : 520));

const TONE = {
  good: { chip: 'bg-n-teal-9', bg: 'bg-n-teal-3', text: 'text-n-teal-11' },
  warn: { chip: 'bg-n-amber-9', bg: 'bg-n-amber-3', text: 'text-n-amber-11' },
  bad: { chip: 'bg-n-ruby-9', bg: 'bg-n-ruby-3', text: 'text-n-ruby-11' },
  none: { chip: 'bg-n-slate-8', bg: 'bg-n-solid-2', text: 'text-n-slate-12' },
};
const toneCls = node => TONE[node.tone] || TONE.none;
const SENT_EMOJI = { positivo: '🙂', neutro: '😐', negativo: '😠' };
const sentEmoji = n => (n.stage === 'entrada' && n.sentimento ? SENT_EMOJI[n.sentimento] : '');

const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const sinceTs = ref(getUnixStartOfDay(customDateRange.value[0]));
const untilTs = ref(getUnixEndOfDay(customDateRange.value[1]));
// T-252: canal e lado. Nulo e 'todos' são o padrão: quem abre a tela vê a operação inteira.
const canalFiltrado = ref(null);
const entrada = ref('assunto');
// T-53 (ata 06/08): segmentar o relatório por parceiro — "como é a jornada dos casos que
// dependeram da FENG?". Vazio = todos.
const parceiroFiltro = ref('');
const parceirosLista = ref([]);

const data = reactive({ total: 0, stages: [], links: [], kpis: {}, csat: null });
const hoveredKey = ref(null);
const lockedKey = ref(null);
const activeKey = computed(() => hoveredKey.value || lockedKey.value);
const hasHi = computed(() => !!activeKey.value);

const pct = v => (data.total ? Math.round((v * 100) / data.total) : 0);

const layout = computed(() => {
  const stages = data.stages || [];
  if (!stages.length) return { nodes: [], ribbons: [], width: 0 };
  const unit = (flowH.value - 9 * GAP) / Math.max(data.total, 1);
  const map = {};
  stages.forEach((st, ci) => {
    const sum = st.nodes.reduce((a, n) => a + n.value, 0);
    const stackH = sum * unit + Math.max(st.nodes.length - 1, 0) * GAP;
    let y = (flowH.value - stackH) / 2;
    st.nodes.forEach(n => {
      const h = Math.max(n.value * unit, 3);
      map[n.key] = { ...n, x: ci * (NODEW + COLGAP), y, h, srcUsed: 0, tgtUsed: 0 };
      y += h + GAP;
    });
  });
  const ribbons = [];
  (data.links || []).forEach(l => {
    const s = map[l.from];
    const d = map[l.to];
    if (!s || !d) return;
    const w = l.value * unit;
    const y0 = s.y + s.srcUsed;
    const y1 = d.y + d.tgtUsed;
    s.srcUsed += w;
    d.tgtUsed += w;
    const x0 = s.x + NODEW;
    const x1 = d.x;
    const cx = (x0 + x1) / 2;
    ribbons.push({
      from: l.from,
      to: l.to,
      red: d.tone === 'bad',
      // A fita herda o tom do DESTINO: é o desfecho que interessa ler no gradiente.
      tone: d.tone || 'none',
      path: `M ${x0},${y0} C ${cx},${y0} ${cx},${y1} ${x1},${y1} L ${x1},${y1 + w} C ${cx},${y1 + w} ${cx},${y0 + w} ${x0},${y0 + w} Z`,
    });
  });
  const width = stages.length * (NODEW + COLGAP) - COLGAP + 110;
  return { nodes: Object.values(map), ribbons, width };
});

const headers = computed(() =>
  (data.stages || []).map((st, ci) => ({
    key: st.key,
    label: st.label,
    time: st.time,
    gargalo: st.gargalo,
    x: ci * (NODEW + COLGAP),
  }))
);

const chips = computed(() => {
  const entradaStage = (data.stages || []).find(s => s.key === 'entrada');
  return entradaStage ? entradaStage.nodes : [];
});

const kpis = computed(() => {
  const k = data.kpis || {};
  const d = k.delta || {};
  const list = [
    { key: 'TOTAL', value: (k.total ?? 0).toLocaleString('pt-BR'), tone: 'none' },
    { key: 'AUTO', value: `${k.autoatendimento ?? 0}%`, tone: 'good', delta: d.autoatendimento },
    // Transbordo é o único aqui que MELHORA quando cai. Antes o comparativo pintava qualquer
    // alta de verde, e "transbordo subiu 12 pontos" aparecia como boa notícia.
    { key: 'TRANSBORDO', value: `${k.transbordo ?? 0}%`, tone: 'warn', delta: d.transbordo, melhorQuando: 'desce' },
    { key: 'PENDENCIA', value: `${k.pendencia ?? 0}%`, tone: 'warn' },
    { key: 'RESOLVIDAS', value: `${k.resolvidas_humano ?? 0}%`, tone: 'good', delta: d.resolvidas_humano },
  ];
  if (data.csat) {
    list.push({
      key: 'CSAT',
      value: `${data.csat.media.toLocaleString('pt-BR')}★`,
      tone: 'good',
      note: `${data.csat.respostas} ${t('JOURNEY.CSAT_RESP')}`,
    });
  }
  return list;
});

const neighbors = computed(() => {
  const key = activeKey.value;
  const set = new Set(key ? [key] : []);
  if (key) {
    (data.links || []).forEach(l => {
      if (l.from === key) set.add(l.to);
      if (l.to === key) set.add(l.from);
    });
  }
  return set;
});
const isHi = r => activeKey.value && (r.from === activeKey.value || r.to === activeKey.value);
// A cor da fita NÃO pode vir de CSS. O html-to-image clona o <svg> com cloneNode e não injeta
// estilo computado nos filhos, então regra de folha de estilo se perde na exportação e o path
// cai no fill padrão do SVG, que é PRETO — foi exatamente o que saiu no PNG. Atributo e style
// inline sobrevivem ao clone (verificado com html-to-image 1.11.13 + Chrome).
//
// A fita nasce quase invisível na origem e ganha a cor do destino ao chegar. Isso limpa a
// região de cruzamento, que era a queixa, e de quebra faz a cor carregar informação: dá para
// ver de longe quanto do fluxo termina em resolvido, abandonado ou crítico.
// Passo 9 da paleta, o mesmo do chip do nó. Hex fixo em vez de token porque o valor precisa
// existir dentro do SVG exportado; nesses três tons, claro e escuro usam o mesmo valor.
const TONE_HEX = {
  good: 'rgb(18, 165, 148)',
  warn: 'rgb(255, 197, 61)',
  bad: 'rgb(229, 70, 102)',
  none: 'rgb(139, 141, 152)',
};
const toneHex = tone => TONE_HEX[tone] || TONE_HEX.none;
// Com o mouse em cima de um nó, a fita ligada a ele fica âmbar chapado: o gradiente é sutil
// demais para servir de destaque.
const ribbonFill = (r, i) => (isHi(r) ? 'rgba(230, 160, 40, 0.6)' : `url(#jfg-${i})`);
// Apagar as demais só faz sentido com o mouse em cima; no arquivo exportado não há hover.
const ribbonOpacity = r => (hasHi.value && !isHi(r) ? 0.22 : 1);
const isDim = key => hasHi.value && !neighbors.value.has(key);

const nodeStyle = n => ({
  left: `${n.x}px`,
  top: `${n.y}px`,
  width: `${NODEW}px`,
  height: `${Math.max(n.h, 6)}px`,
});
const outside = n => n.h < 22;
const compact = n => n.h >= 22 && n.h < 50;

const fetchData = async () => {
  try {
    const { data: res } = await JourneyAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
      entrada: entrada.value,
      parceiro: parceiroFiltro.value || undefined,
      inboxId: canalFiltrado.value,
    });
    data.total = res.total || 0;
    data.stages = res.stages || [];
    data.links = res.links || [];
    data.kpis = res.kpis || {};
    data.csat = res.csat || null;
  } catch (e) {
    useAlert(t('JOURNEY.ERROR'));
  }
};

// T-133: taxa de recontato. Fica aqui porque é a mesma pergunta da jornada, só do outro lado:
// o que aconteceu DEPOIS que o time deu o caso por resolvido. Falha aqui não derruba o painel.
const recontato = ref(null);
const janelaRecontato = ref(7);

const fetchRecontato = async () => {
  try {
    const { data } = await RecontatoAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
      janela: janelaRecontato.value,
    });
    recontato.value = data;
  } catch (e) {
    recontato.value = null;
  }
};

const setJanelaRecontato = dias => {
  if (janelaRecontato.value === dias) return;
  janelaRecontato.value = dias;
  fetchRecontato();
};

const onDateRangeChange = value => {
  const [start, end, rangeType] = value;
  customDateRange.value = [start, end];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  sinceTs.value = getUnixStartOfDay(start);
  untilTs.value = getUnixEndOfDay(end);
  fetchData();
  fetchRecontato();
};

const setParceiro = slug => {
  if (parceiroFiltro.value === slug) return;
  parceiroFiltro.value = slug;
  lockedKey.value = null;
  fetchData();
};

const carregarParceiros = async () => {
  try {
    const { data } = await ParceirosAPI.get();
    parceirosLista.value = [...(data.externa || []), ...(data.interna || [])];
  } catch (e) {
    parceirosLista.value = [];
  }
};

const setEntrada = mode => {
  if (entrada.value === mode) return;
  entrada.value = mode;
  lockedKey.value = null;
  fetchData();
};
// Drill-down (Fase 3): clicar num nó abre a lista de conversas daquele segmento.
const drill = reactive({
  open: false, node: null, convs: [], loading: false, ordem: 'antigas',
  // total = quantas existem no segmento (não quantas foram baixadas). A distinção importa:
  // antes o painel exibia o tamanho da lista cortada como se fosse o total.
  total: 0, pagina: 1, temMais: false, carregandoMais: false,
});

// Ordenações que o supervisor pediu: por status (agrupando o que está em aberto) e por
// idade. O padrão é "mais antigas primeiro" — numa lista de pendência, o topo tem que ser
// o caso mais esquecido, não o mais recente.
const ORDENS = ['antigas', 'recentes', 'movimentacao', 'status'];
const PESO_STATUS = { open: 0, pending: 1, snoozed: 2, resolved: 3 };
const ts = v => (v ? new Date(v).getTime() : 0);
const convsOrdenadas = computed(() => {
  const lista = [...drill.convs];
  if (drill.ordem === 'recentes') return lista.sort((a, b) => ts(b.criado_em) - ts(a.criado_em));
  if (drill.ordem === 'movimentacao') {
    return lista.sort((a, b) => ts(a.movimentado_em) - ts(b.movimentado_em));
  }
  if (drill.ordem === 'status') {
    return lista.sort(
      (a, b) =>
        (PESO_STATUS[a.status] ?? 9) - (PESO_STATUS[b.status] ?? 9) ||
        ts(a.criado_em) - ts(b.criado_em)
    );
  }
  return lista.sort((a, b) => ts(a.criado_em) - ts(b.criado_em));
});

const fmtData = v => {
  if (!v) return '—';
  const d = new Date(v);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const openDrill = async node => {
  // T-261: clicar TRAVA o caminho, e não só abre a lista. Antes o destaque vinha do hover, então
  // ele sumia no instante em que o mouse ia para o botão de exportar: a imagem saía sem a
  // seleção que a pessoa queria mostrar. Travando no clique, o caminho continua marcado com o
  // mouse em qualquer lugar, inclusive no PNG.
  lockedKey.value = node.key;
  drill.open = true;
  drill.node = node;
  drill.convs = [];
  drill.loading = true;
  try {
    const { data: res } = await JourneyAPI.conversations({
      node: node.key,
      since: sinceTs.value,
      until: untilTs.value,
      entrada: entrada.value,
      parceiro: parceiroFiltro.value || undefined,
      pagina: 1,
    });
    drill.convs = res.conversas || [];
    drill.total = res.total ?? drill.convs.length;
    drill.pagina = res.pagina || 1;
    drill.temMais = !!res.tem_mais;
  } catch (e) {
    useAlert(t('JOURNEY.ERROR'));
  } finally {
    drill.loading = false;
  }
};

const carregarMais = async () => {
  if (drill.carregandoMais || !drill.temMais) return;
  drill.carregandoMais = true;
  try {
    const { data: res } = await JourneyAPI.conversations({
      node: drill.node.key,
      since: sinceTs.value,
      until: untilTs.value,
      entrada: entrada.value,
      parceiro: parceiroFiltro.value || undefined,
      pagina: drill.pagina + 1,
    });
    drill.convs = [...drill.convs, ...(res.conversas || [])];
    drill.pagina = res.pagina || drill.pagina + 1;
    drill.temMais = !!res.tem_mais;
  } catch (e) {
    useAlert(t('JOURNEY.ERROR'));
  } finally {
    drill.carregandoMais = false;
  }
};
// Nova guia: o supervisor está analisando um segmento inteiro; abrir na mesma aba faria
// ele perder o relatório a cada conversa que quisesse inspecionar.
const goToConversation = displayId => {
  const url = frontendURL(
    conversationUrl({ accountId: route.params.accountId, id: displayId })
  );
  window.open(url, '_blank', 'noopener');
};

// Export (Fase 4)
const flowEl = ref(null);
const csvCell = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
const exportCsv = () => {
  const rows = [['Etapa', 'Nó', 'Quantidade', 'Percentual']];
  (data.stages || []).forEach(st =>
    st.nodes.forEach(n => rows.push([st.label, n.label, n.value, `${pct(n.value)}%`]))
  );
  rows.push([]);
  rows.push(['Origem', 'Destino', 'Quantidade']);
  const labels = {};
  (data.stages || []).forEach(st =>
    st.nodes.forEach(n => {
      labels[n.key] = n.label;
    })
  );
  (data.links || []).forEach(l =>
    rows.push([labels[l.from] || l.from, labels[l.to] || l.to, l.value])
  );
  downloadCsvFile(
    'jornada-do-torcedor.csv',
    rows.map(r => r.map(csvCell).join(',')).join('\n')
  );
};
// T-93 (reunião 13/08): a exportação vira "visão executiva". Antes o PNG herdava o fundo do
// tema (transparente/escuro, que no preview aparecia preto) e as fitas saíam pesadas demais.
// Este relatório é o que a Tamara manda para gerente e diretor, então o arquivo tem vida
// própria: fundo branco e traço mais leve, independentemente do tema de quem exporta.
// A tela NÃO muda: a classe de impressão só existe durante o clique.
const printing = ref(false);
const exportPng = async () => {
  if (!flowEl.value) return;
  printing.value = true;
  // Espera o Vue aplicar a classe antes de fotografar, senão a captura pega o estado antigo.
  await nextTick();
  try {
    const dataUrl = await toPng(flowEl.value, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#ffffff',
    });
    const link = document.createElement('a');
    link.download = 'jornada-do-torcedor.png';
    link.href = dataUrl;
    link.click();
  } catch (e) {
    useAlert(t('JOURNEY.ERROR'));
  } finally {
    printing.value = false;
  }
};

const toggleExpand = () => {
  expanded.value = !expanded.value;
  viewportH.value = window.innerHeight;
};
const onResize = () => {
  viewportH.value = window.innerHeight;
};
const onKey = e => {
  if (e.key === 'Escape') expanded.value = false;
};

onMounted(() => {
  fetchData();
  fetchRecontato();
  carregarParceiros();
  window.addEventListener('resize', onResize);
  window.addEventListener('keydown', onKey);
});
onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('keydown', onKey);
});
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <div class="flex flex-col gap-3 mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">{{ t('JOURNEY.HEADER') }}</h1>
      <p class="text-sm text-n-slate-11 -mt-1">{{ t('JOURNEY.HINT') }}</p>
      <div class="flex items-start flex-wrap gap-3">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
        <!-- T-260: baixa exatamente o que esta tela está mostrando. -->
        <!-- T-252: canal e lado. Trocar qualquer um recarrega a tela. -->
        <FiltrosRelatorio
          v-model:inbox-id="canalFiltrado"
          :com-lado="false"
          @update:inbox-id="fetchData"
        />
        <BaixarRelatorio
          nome="jornada"
          :dados="data"
          :since="sinceTs"
          :until="untilTs"
        />
        <!-- T-260 (leitura A): o caminho para a quebra completa, que já existe numa tela só. -->
        <AtalhoAnalitico />
      </div>
    </div>

    <!-- T-133: taxa de recontato. Primeiro bloco de propósito: é a pergunta que a Tamara faz
         para medir o time (resolveu mesmo ou o torcedor voltou?). -->
    <section
      v-if="recontato && recontato.avaliadas > 0"
      class="flex flex-col gap-4 p-5 mb-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('JOURNEY.RECONTATO.TITULO') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{
              t('JOURNEY.RECONTATO.RESUMO', {
                recontatos: recontato.recontatos,
                avaliadas: recontato.avaliadas,
                dias: recontato.janela_dias,
              })
            }}
          </p>
        </div>
        <div class="flex items-center gap-1 p-1 rounded-lg bg-n-alpha-1">
          <button
            v-for="dias in recontato.janelas_disponiveis"
            :key="dias"
            type="button"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="
              janelaRecontato === dias
                ? 'bg-n-solid-1 text-n-slate-12 font-medium shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="setJanelaRecontato(dias)"
          >
            {{ t('JOURNEY.RECONTATO.JANELA', { dias }) }}
          </button>
        </div>
      </div>

      <div class="flex items-baseline gap-2">
        <span class="text-3xl font-semibold text-n-slate-12">
          {{ recontato.taxa }}%
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('JOURNEY.RECONTATO.TAXA_LABEL') }}
        </span>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div
          v-for="grupo in [
            { chave: 'canal', linhas: recontato.por_canal },
            { chave: 'agente', linhas: recontato.por_agente },
          ]"
          :key="grupo.chave"
          class="flex flex-col gap-2"
        >
          <span class="text-xs font-medium uppercase text-n-slate-11">
            {{ t(`JOURNEY.RECONTATO.POR_${grupo.chave.toUpperCase()}`) }}
          </span>
          <div
            v-for="linha in grupo.linhas"
            :key="`${grupo.chave}-${linha.id}`"
            class="flex items-center justify-between gap-3 py-1 text-sm border-b border-n-weak last:border-b-0"
          >
            <span class="truncate text-n-slate-12">{{ linha.nome }}</span>
            <span class="flex-shrink-0 text-n-slate-11">
              {{
                t('JOURNEY.RECONTATO.LINHA', {
                  recontatos: linha.recontatos,
                  avaliadas: linha.avaliadas,
                  taxa: linha.taxa,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- KPIs -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
      <div
        v-for="k in kpis"
        :key="k.key"
        class="flex flex-col gap-0.5 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs font-medium text-n-slate-11">{{ t(`JOURNEY.KPI.${k.key}`) }}</span>
        <span class="text-2xl font-semibold tabular-nums" :class="TONE[k.tone].text">{{ k.value }}</span>
        <DeltaPeriodo
          :valor="k.delta ?? null"
          :melhor-quando="k.melhorQuando || 'sobe'"
        />
        <span v-if="k.note" class="text-xs font-medium text-n-slate-10">{{ k.note }}</span>
      </div>
    </div>

    <!-- Wrapper que vira tela cheia -->
    <div
      :class="
        expanded
          ? 'fixed inset-0 z-[70] bg-n-solid-1 p-4 overflow-auto flex flex-col gap-2'
          : ''
      "
    >
      <!-- Controles: toggle entrada + chips + tela cheia -->
      <div class="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div class="flex items-center gap-2">
          <span class="text-xs font-semibold uppercase tracking-wide text-n-slate-11">{{ t('JOURNEY.ENTRY') }}</span>
          <div class="inline-flex p-0.5 rounded-lg bg-n-alpha-1 border border-n-weak gap-0.5">
            <button
              v-for="m in ['assunto', 'canal']"
              :key="m"
              class="px-3 py-1 text-sm font-medium rounded-md"
              :class="entrada === m ? 'bg-n-solid-3 text-n-slate-12 shadow-sm' : 'text-n-slate-11'"
              @click="setEntrada(m)"
            >
              {{ t(`JOURNEY.ENTRY_${m.toUpperCase()}`) }}
            </button>
          </div>
          <template v-if="parceirosLista.length">
            <span class="text-xs font-semibold uppercase tracking-wide text-n-slate-11 ml-2">
              {{ t('JOURNEY.PARCEIRO') }}
            </span>
            <select
              :value="parceiroFiltro"
              class="px-2 py-1 text-sm rounded-md bg-n-alpha-1 border border-n-weak text-n-slate-12"
              @change="setParceiro($event.target.value)"
            >
              <option value="">{{ t('JOURNEY.PARCEIRO_TODOS') }}</option>
              <option v-for="p in parceirosLista" :key="p.slug" :value="p.slug">
                {{ p.nome }}
              </option>
            </select>
          </template>
          <!-- T-261: o Destacar era uma fileira de botões na DIREITA, e a cortina lateral do
               drill cobria exatamente eles. Virou seletor ao lado do de parceiro, à esquerda,
               onde nada passa por cima. -->
          <template v-if="chips.length">
            <span
              class="ml-2 text-xs font-semibold tracking-wide uppercase text-n-slate-11"
            >
              {{ t('JOURNEY.HIGHLIGHT') }}
            </span>
            <select
              :value="lockedKey || ''"
              class="px-2 py-1 text-sm border rounded-md bg-n-alpha-1 border-n-weak text-n-slate-12"
              @change="lockedKey = $event.target.value || null"
            >
              <option value="">{{ t('JOURNEY.ALL') }}</option>
              <option v-for="c in chips" :key="c.key" :value="c.key">
                {{ c.label }}
              </option>
            </select>
          </template>
        </div>
        <div class="flex items-center gap-2 flex-wrap">
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-6"
            @click="exportCsv"
          >
            <span class="i-lucide-download size-4 shrink-0" /> CSV
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-6"
            @click="exportPng"
          >
            <span class="i-lucide-image size-4 shrink-0" /> PNG
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-6"
            @click="toggleExpand"
          >
            <span :class="expanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'" class="size-4 shrink-0" />
            {{ expanded ? t('JOURNEY.EXIT_FULLSCREEN') : t('JOURNEY.FULLSCREEN') }}
          </button>
        </div>
      </div>

      <!-- Board -->
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
        :class="{ 'flex-1 min-h-0 flex flex-col': expanded }"
      >
      <div v-if="!data.total" class="py-16 text-center text-sm text-n-slate-11">
        {{ t('JOURNEY.EMPTY') }}
      </div>
      <div v-else class="overflow-x-auto">
        <div ref="flowEl" class="p-2" :class="{ 'jf-print': printing }" :style="{ width: `${layout.width}px` }">
          <!-- headers -->
          <div class="relative" style="height: 58px">
            <div
              v-for="h in headers"
              :key="h.key"
              class="absolute top-0"
              :style="{ left: `${h.x}px`, width: `${NODEW}px` }"
            >
              <div class="text-sm font-semibold text-n-slate-12 flex items-center gap-1.5">
                {{ h.label }}
                <span
                  v-if="h.gargalo"
                  class="text-[10px] uppercase tracking-wide font-bold text-white bg-n-ruby-9 rounded-full px-1.5 py-px"
                >
                  {{ t('JOURNEY.GARGALO') }}
                </span>
              </div>
              <div v-if="h.time" class="text-[11px] font-medium text-n-amber-11 mt-0.5">⏱ {{ h.time }}</div>
            </div>
          </div>
          <!-- flow -->
          <div class="relative" :style="{ height: `${flowH}px` }">
            <svg
              class="absolute inset-0 w-full h-full overflow-visible"
              :viewBox="`0 0 ${layout.width} ${flowH}`"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  v-for="(r, i) in layout.ribbons"
                  :id="`jfg-${i}`"
                  :key="`jfg-${i}`"
                  x1="0"
                  x2="1"
                >
                  <stop offset="0" :stop-color="toneHex(r.tone)" stop-opacity="0.05" />
                  <stop offset="1" :stop-color="toneHex(r.tone)" stop-opacity="0.3" />
                </linearGradient>
              </defs>
              <path
                v-for="(r, i) in layout.ribbons"
                :key="i"
                :d="r.path"
                class="jf-ribbon"
                :fill="ribbonFill(r, i)"
                :opacity="ribbonOpacity(r)"
              />
            </svg>
            <div
              v-for="n in layout.nodes"
              :key="n.key"
              class="absolute rounded-lg border border-n-weak overflow-hidden flex flex-col justify-center pl-3 pr-2 py-1 transition-opacity cursor-pointer"
              :class="[toneCls(n).bg, { 'opacity-30': isDim(n.key), 'ring-2 ring-n-amber-9': activeKey === n.key }]"
              :style="nodeStyle(n)"
              :title="`${n.label}: ${n.value.toLocaleString('pt-BR')} (${pct(n.value)}%) — clique para ver as conversas`"
              @mouseenter="hoveredKey = n.key"
              @mouseleave="hoveredKey = null"
              @click="openDrill(n)"
            >
              <span class="absolute left-0 top-0 bottom-0 w-1" :class="toneCls(n).chip" />
              <template v-if="outside(n)">
                <span class="absolute left-3 whitespace-nowrap text-xs font-semibold text-n-slate-12">
                  {{ n.label }}
                  <span class="tabular-nums" :class="toneCls(n).text">{{ n.value }}</span>
                  <span class="text-n-slate-10">{{ pct(n.value) }}%</span>
                </span>
              </template>
              <template v-else-if="compact(n)">
                <div class="flex items-baseline gap-1.5 min-w-0">
                  <span class="text-xs font-semibold text-n-slate-12 truncate"><span v-if="sentEmoji(n)" class="mr-0.5">{{ sentEmoji(n) }}</span>{{ n.label }}</span>
                  <span class="text-xs font-bold tabular-nums shrink-0" :class="toneCls(n).text">
                    {{ n.value.toLocaleString('pt-BR') }}
                  </span>
                  <span class="text-[11px] text-n-slate-10 shrink-0">{{ pct(n.value) }}%</span>
                </div>
              </template>
              <template v-else>
                <span class="text-xs font-semibold text-n-slate-12 truncate"><span v-if="sentEmoji(n)" class="mr-0.5">{{ sentEmoji(n) }}</span>{{ n.label }}</span>
                <span class="text-sm font-bold tabular-nums" :class="toneCls(n).text">
                  {{ n.value.toLocaleString('pt-BR') }}
                  <span class="text-[11px] font-semibold text-n-slate-10">{{ pct(n.value) }}%</span>
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- legenda -->
      <div class="flex flex-wrap gap-4 items-center pt-3 text-xs text-n-slate-11">
        <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded bg-n-teal-9" /> {{ t('JOURNEY.LEGEND_GOOD') }}</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded bg-n-amber-9" /> {{ t('JOURNEY.LEGEND_WARN') }}</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded bg-n-ruby-9" /> {{ t('JOURNEY.LEGEND_BAD') }}</span>
        <span class="inline-flex items-center gap-1.5"><span class="w-5 h-2 rounded bg-n-slate-6" /> {{ t('JOURNEY.LEGEND_RIBBON') }}</span>
      </div>
      </div>
    </div>

    <!-- Drill-down: conversas do segmento clicado -->
    <div v-if="drill.open" class="fixed inset-0 z-[80]">
      <div class="absolute inset-0 bg-black/30" @click="drill.open = false"></div>
      <div
        class="absolute right-0 top-0 bottom-0 w-full max-w-md bg-n-solid-1 border-l border-n-weak shadow-xl flex flex-col"
      >
        <div class="flex items-start justify-between px-5 py-3 border-b border-n-weak">
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">{{ drill.node?.label }}</h3>
            <p class="text-xs text-n-slate-11">
              <template v-if="drill.total > drill.convs.length">
                {{ t('JOURNEY.DRILL_PARCIAL', { exibidas: drill.convs.length, total: drill.total }) }}
              </template>
              <template v-else>{{ drill.total }} {{ t('JOURNEY.DRILL_COUNT') }}</template>
            </p>
          </div>
          <button class="p-1 text-n-slate-11 hover:text-n-slate-12" @click="drill.open = false">
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <div class="flex-1 overflow-auto">
          <div
            v-if="!drill.loading && drill.convs.length"
            class="flex items-center gap-2 px-5 py-2 border-b border-n-weak"
          >
            <span class="text-xs text-n-slate-11">{{ t('JOURNEY.DRILL_ORDER') }}</span>
            <button
              v-for="o in ORDENS"
              :key="o"
              class="px-2 py-0.5 text-xs rounded-md"
              :class="
                drill.ordem === o
                  ? 'bg-n-slate-4 text-n-slate-12 font-medium'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="drill.ordem = o"
            >
              {{ t(`JOURNEY.DRILL_ORDER_${o.toUpperCase()}`) }}
            </button>
          </div>
          <div v-if="drill.loading" class="p-6 text-sm text-n-slate-11">{{ t('JOURNEY.DRILL_LOADING') }}</div>
          <div v-else-if="!drill.convs.length" class="p-6 text-sm text-n-slate-11">
            {{ t('JOURNEY.DRILL_EMPTY') }}
          </div>
          <button
            v-for="c in convsOrdenadas"
            :key="c.display_id"
            class="w-full text-left px-5 py-3 border-b border-n-weak hover:bg-n-alpha-1 flex items-center justify-between gap-3"
            @click="goToConversation(c.display_id)"
          >
            <div class="min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">{{ c.contato || '—' }}</div>
              <div class="text-xs text-n-slate-11">TKT-{{ c.display_id }} · {{ c.assunto }}</div>
              <div class="text-xs text-n-slate-10">
                {{ t('JOURNEY.DRILL_ABERTA') }} {{ fmtData(c.criado_em) }}
                · {{ t('JOURNEY.DRILL_MOVIMENTADA') }} {{ fmtData(c.movimentado_em) }}
              </div>
            </div>
            <span
              class="text-xs shrink-0"
              :class="c.status === 'resolved' ? 'text-n-teal-11' : 'text-n-amber-11'"
            >
              {{ statusLabel(c.status) }}
            </span>
          </button>
          <div v-if="drill.temMais" class="p-4 text-center">
            <button
              class="px-3 py-1.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3"
              :disabled="drill.carregandoMais"
              @click="carregarMais"
            >
              {{ drill.carregandoMais ? t('JOURNEY.DRILL_LOADING') : t('JOURNEY.DRILL_MAIS') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.jf-ribbon {
  /* Cor e opacidade vêm em atributo (ver ribbonFill): CSS não chega no PNG exportado. */
  transition:
    fill 0.16s ease,
    opacity 0.16s ease;
}
@media (prefers-reduced-motion: reduce) {
  .jf-ribbon {
    transition: none;
  }
}

/* T-93 — modo impressão: vale só durante a exportação do PNG. Fundo branco, para o relatório
   funcionar fora do produto (slide, e-mail, impressão). As fitas já saem translúcidas porque
   a cor delas é atributo. Usa :deep porque o estilo é scoped e precisa alcançar os nós. */
.jf-print {
  background: #ffffff;
}
/* Quem exporta pode estar no tema escuro. Sem isto o PNG sairia com fundo branco e caixas
   escuras: pior do que estava. Fixamos os tons claros das mesmas cores para o arquivo sair
   igual, exportado por quem for. */
.jf-print :deep(.text-n-slate-12) {
  color: #1c1c1e;
}
.jf-print :deep(.text-n-slate-11),
.jf-print :deep(.text-n-slate-10) {
  color: #5b5b62;
}
.jf-print :deep(.bg-n-teal-3) {
  background-color: #e0f8f3;
}
.jf-print :deep(.bg-n-amber-3) {
  background-color: #fff7c2;
}
.jf-print :deep(.bg-n-ruby-3) {
  background-color: #ffe9ed;
}
.jf-print :deep(.bg-n-solid-2) {
  background-color: #f7f7f8;
}
.jf-print :deep(.text-n-teal-11) {
  color: #0f766e;
}
.jf-print :deep(.text-n-amber-11) {
  color: #a16207;
}
.jf-print :deep(.text-n-ruby-11) {
  color: #be123c;
}
.jf-print :deep(.border-n-weak) {
  border-color: #e4e4e7;
}
</style>
